import React, {memo, useEffect} from "react";
import styles from "./styles.module.scss";
import {TextMedium14, ThirtyText} from "@/app/components/texts";
import {t} from "i18next";
import {s} from "@/app/utils/s";
import Checkbox from "@/app/components/checkbox";
import useNearbyPlayers from "@/app/features/find_players/components/people_near_you/slice";
import CircularProgressIndicator from "@/app/components/circular_progress_indicator";
import LazyAssets from "@/app/utils/assets_widget";
import {Outlet} from "react-router-dom";
import {useMediaQuery} from "@/app/utils/custom_function";
import DetailPlayerCard from "src/app/components/detail_player_card";
import classNames from "classnames";
import Button from "@/app/components/button";
import UserRowWidget from "@/app/components/user_row_widget";
import {NearbyUsersModel} from "@/app/api/user";
import Space from "@/app/components/space";

interface Props {
  inChat?: boolean,
  onClick?: (user: NearbyUsersModel | undefined) => void,
  withoutButtons?: boolean;
}

const PeopleNearYou: React.FC<Props> = ({inChat = false, onClick, withoutButtons = false}) => {
  const {
    state,
    init,
    loadMore,
    changeSelectSeveral,
    toggleUser,
    openUserDetails,
    goToCreateAMatch,
    createChat,
    goToCreateAMatchSingle,
  } = useNearbyPlayers();
  const isDesktop = useMediaQuery('(min-width:1024px)');

  useEffect(() => {
    init()
  }, [init]);

  useEffect(() => {
    if (inChat) return;
    const handleScroll = () => {
      if (state.loadingMore || state.hasLoading || state.isAll) return;
      const nearBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 200;
      if (nearBottom) {
        loadMore();
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [inChat, loadMore, state.hasLoading, state.isAll, state.loadingMore]);

  return (
    state.selectedUser !== undefined
      ? <div className={styles.detailPage}>
        <Outlet context={state.selectedUser}/>
      </div>
      : <div className={classNames(styles.peopleNearYou, {[styles.inChat]: inChat})}>
        <div className={styles.header}>
          <ThirtyText text={t(s.peopleNearYouReadyToPlay)}/>
          {!inChat && <Checkbox<string> items={[s.inviteSeveralPeople]} onChange={changeSelectSeveral} useS={true}
                                        value={state.selectSeveral ? [s.inviteSeveralPeople] : []}/>}
        </div>

        {state.hasError && <div className={styles.error}>{state.hasError}</div>}
        {state.hasLoading && <div className={styles.centered}><CircularProgressIndicator isGreen/></div>}
        {(state.users === undefined || state.users.length === 0) &&
            <div className={styles.centered}><ThirtyText text={t(s.noPlayersNearYou)}/></div>}
        {!isDesktop && <div className={styles.users} onScroll={(e) => {
          const target = e.currentTarget;
          if (target.scrollTop + target.clientHeight >= target.scrollHeight - 50) {
            loadMore();
          }
        }}>
          {state.users?.map((user) => (
            <UserRowWidget state={state} toggleUser={toggleUser} user={user} key={user.id} className={styles.user}
                           onClick={onClick ? (user) => onClick(user) : () => {
                             openUserDetails(user)
                           }}/>
          ))}
          {state.loadingMore && <div className={styles.centered}><CircularProgressIndicator isGreen/></div>}
        </div>}
        {isDesktop && <div
            className={classNames(styles.users, {[styles.onlyOne]: state?.users?.length === 1}, {[styles.onlyTwo]: state?.users?.length === 2})}>
          {state.users?.map((user) => <div
            className={classNames(styles.userWrapper, {[styles.selected]: state.selectedUsers?.includes(user.id)})}>
            {state.selectSeveral &&
                <Checkbox<number> items={[user.id]} onChange={toggleUser} useS={false} className={styles.checkbox}
                                  onlyBox value={state.selectedUsers} bg/>}
            <DetailPlayerCard user={user} key={user.id} className={styles.user} isCard
                              onClick={onClick ? (user) => onClick(user) :
                                state.selectSeveral ? () => toggleUser(user.id) : () => openUserDetails(user)
                              }
                              createChat={withoutButtons ? undefined : createChat}
                              goToCreateAMatch={withoutButtons ? undefined : goToCreateAMatchSingle}
                              hasLoading={state.chatLoadingUsers.includes(user.id)}
            />
          </div>)}
          {state.loadingMore && <div className={styles.centered}><CircularProgressIndicator isGreen/></div>}
        </div>}
        {(!state.isAll && !state.users) && <div className={styles.more} onClick={loadMore}>
            <TextMedium14 text={t(s.moreCount, {count: 10})} className={styles.text}/>
            <LazyAssets.ChevronBottom className={styles.icon}/>
        </div>}
        {!inChat && <div className={styles.buttonWrapper}>
            <Button text={t(s.createAMatch)} onClick={() =>
              goToCreateAMatch(state.selectedUsers)} notExpanded={isDesktop} className={styles.button}
                    isDisabled={(state.selectedUsers?.length ?? 0) <= 0}/>
        </div>}
      </div>

  );
};

export default memo(PeopleNearYou);